import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Link2, Mail, RotateCcw, XCircle, RefreshCw, Plus } from "lucide-react";
import api, { apiErr, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, AdminHeading, adminInput, Th, Td } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EmailLinkDialog from "@/components/admin/EmailLinkDialog";

const STATUSES = ["Active", "Pending", "Paid", "Failed", "Cancelled", "Expired", "Refunded", "Partially Refunded"];
const TYPES = ["reservation", "event", "membership"];

function StatusPill({ s }) {
  const cls = s === "Paid" ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"
    : ["Cancelled", "Expired", "Failed"].includes(s) ? "text-red-300 border-red-400/40 bg-red-400/10"
    : "text-amber-300 border-amber-400/40 bg-amber-400/10";
  return <span className={`status-pill border ${cls}`} data-testid="pr-status">{s}</span>;
}

function NewRequestDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("event");
  const [levels, setLevels] = useState([]);
  const [f, setF] = useState({ association_id: "", level_id: "", purpose: "event_deposit", amount: "", recipient_email: "", recipient_name: "", due_date: "", guest_message: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (open) api.get("/admin/memberships/levels").then((r) => setLevels(r.data)).catch(() => {}); }, [open]);

  const purposes = type === "event"
    ? [["event_deposit", "Event Deposit"], ["event_balance", "Event Balance"], ["event_full", "Full Event Balance"], ["event_installment", "Approved Installment"], ["event_addon", "Event Add-On"], ["custom", "Approved Custom Amount"]]
    : [["membership_initial", "Initial Membership Payment"], ["membership_deposit", "Membership Deposit"], ["renewal", "Renewal Payment"], ["membership_installment", "Approved Installment"], ["custom", "Approved Custom Amount"]];
  const needAmount = ["event_installment", "event_addon", "custom", "membership_installment"].includes(f.purpose);

  const save = async () => {
    setSaving(true);
    try {
      if (type === "event") {
        await api.post(`/admin/event-payments/${f.association_id}/request`, {
          purpose: f.purpose, amount: needAmount ? parseFloat(f.amount) : undefined,
          recipient_email: f.recipient_email || undefined, recipient_name: f.recipient_name || undefined,
          due_date: f.due_date || undefined, guest_message: f.guest_message || undefined });
      } else {
        await api.post(`/admin/memberships/payment-request`, {
          level_id: f.level_id, purpose: f.purpose, amount: needAmount ? parseFloat(f.amount) : undefined,
          recipient_email: f.recipient_email, recipient_name: f.recipient_name || undefined,
          due_date: f.due_date || undefined, guest_message: f.guest_message || undefined });
      }
      toast.success("Payment request created."); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-gold px-4 py-2 text-sm flex items-center gap-2" data-testid="new-request-btn"><Plus size={15} /> New Payment Request</button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-white/10">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Payment Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {["event", "membership"].map((t) => (
              <button key={t} onClick={() => { setType(t); set("purpose", t === "event" ? "event_deposit" : "membership_initial"); }}
                className={`px-3 py-1.5 rounded-md text-sm capitalize border ${type === t ? "bg-gold/15 border-gold/50 text-gold" : "border-white/10 text-muted-foreground"}`} data-testid={`type-${t}`}>{t}</button>
            ))}
          </div>
          {type === "event"
            ? <input className={adminInput} placeholder="Event ID" value={f.association_id} onChange={(e) => set("association_id", e.target.value)} data-testid="event-id-input" />
            : <select className={adminInput} value={f.level_id} onChange={(e) => set("level_id", e.target.value)} data-testid="level-select"><option value="">Select membership level…</option>{levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>}
          <select className={adminInput} value={f.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="purpose-select">
            {purposes.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {needAmount && <input className={adminInput} type="number" placeholder="Amount (USD)" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="amount-input" />}
          <input className={adminInput} placeholder="Recipient email" value={f.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} data-testid="recipient-email" />
          <input className={adminInput} placeholder="Recipient name" value={f.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} />
          <input type="date" className={adminInput} value={f.due_date} onChange={(e) => set("due_date", e.target.value)} />
          <textarea className={adminInput} rows={2} placeholder="Guest-facing message (optional)" value={f.guest_message} onChange={(e) => set("guest_message", e.target.value)} />
          <button onClick={save} disabled={saving} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="new-request-save">{saving ? <Loader2 className="animate-spin" size={15} /> : null} Create Request</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentRequestCenter() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ftype, setFtype] = useState("");
  const [fstatus, setFstatus] = useState("");
  const [frecipient, setFrecipient] = useState("");
  const [emailFor, setEmailFor] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (ftype) params.set("association_type", ftype);
    if (frecipient) params.set("recipient", frecipient);
    api.get(`/admin/payments/requests?${params.toString()}`)
      .then((r) => setRows(fstatus ? r.data.filter((x) => x.status === fstatus) : r.data))
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ftype, fstatus, frecipient]);

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Secure link copied."); };
  const regen = async (id) => { try { await api.post(`/admin/payments/requests/${id}/regenerate-link`); toast.success("Secure link regenerated."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const cancel = async (id) => { const reason = window.prompt("Reason for cancellation?"); if (!reason) return; try { await api.post(`/admin/payments/requests/${id}/cancel`, { reason }); toast.success("Request cancelled."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const reissue = async (id) => { const reason = window.prompt("Reason for reissue?"); if (!reason) return; try { await api.post(`/admin/payments/requests/${id}/reissue`, { reason }); toast.success("Request reissued."); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div data-testid="payment-request-center">
      <AdminHeading title="Payment Requests" subtitle="Secure online payment requests across reservations, private events and memberships.">
        <NewRequestDialog onDone={load} />
      </AdminHeading>

      <Panel title="Filters" className="mb-4">
        <div className="flex flex-wrap gap-3">
          <select className={adminInput + " max-w-[180px]"} value={ftype} onChange={(e) => setFtype(e.target.value)} data-testid="filter-type"><option value="">All types</option>{TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}</select>
          <select className={adminInput + " max-w-[180px]"} value={fstatus} onChange={(e) => setFstatus(e.target.value)} data-testid="filter-status"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <input className={adminInput + " max-w-[220px]"} placeholder="Recipient email…" value={frecipient} onChange={(e) => setFrecipient(e.target.value)} data-testid="filter-recipient" />
        </div>
      </Panel>

      <Panel title={`Requests (${rows.length})`}>
        {loading ? <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-gold" /></div>
          : rows.length === 0 ? <p className="text-sm text-muted-foreground">No payment requests found.</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr><Th>Type</Th><Th>Recipient</Th><Th>Purpose</Th><Th>Amount</Th><Th>Status</Th><Th>Comm</Th><Th>Created</Th><Th>Actions</Th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5" data-testid={`pr-row-${r.id}`}>
                      <Td><span className="capitalize">{r.association_type}</span></Td>
                      <Td>{r.recipient_name}<div className="text-xs text-muted-foreground">{r.recipient_email}</div></Td>
                      <Td>{r.purpose_label}</Td>
                      <Td>{money(r.amount)}</Td>
                      <Td><StatusPill s={r.status} /></Td>
                      <Td className="text-xs text-muted-foreground">{r.comm_status || "—"}</Td>
                      <Td className="text-xs">{fmtDate(r.created_at)}</Td>
                      <Td>
                        <div className="flex items-center gap-2 text-xs">
                          {r.checkout_url && <button onClick={() => copy(r.checkout_url)} className="text-gold hover:text-gold/80 flex items-center gap-1" data-testid={`copy-${r.id}`}><Link2 size={12} /> Copy</button>}
                          {["Active", "Pending"].includes(r.status) && <button onClick={() => setEmailFor(r.id)} className="text-pool hover:text-pool/80 flex items-center gap-1" data-testid={`email-${r.id}`}><Mail size={12} /> Email</button>}
                          {r.status === "Expired" && <button onClick={() => regen(r.id)} className="text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid={`regen-${r.id}`}><RefreshCw size={12} /> Regenerate</button>}
                          {!["Paid", "Cancelled"].includes(r.status) && <button onClick={() => cancel(r.id)} className="text-red-300 hover:text-red-200 flex items-center gap-1" data-testid={`cancel-${r.id}`}><XCircle size={12} /> Cancel</button>}
                          {!["Paid"].includes(r.status) && <button onClick={() => reissue(r.id)} className="text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`reissue-${r.id}`}><RotateCcw size={12} /> Reissue</button>}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Panel>

      <EmailLinkDialog requestId={emailFor} open={!!emailFor} onOpenChange={(o) => { if (!o) setEmailFor(null); }} onSent={load} />
    </div>
  );
}
